<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useRoute, useRouter, type LocationQueryValue } from "vue-router";

import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import type { EstimatorReportDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useAuthStore } from "@/stores/auth";
import EstimatorReportComparison from "@/views/comparison/EstimatorReportComparison.vue";

const api = useApi();
const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const workspacePublicId = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);

const isLoading = ref(true);
const reports = ref<EstimatorReportDto[]>([]);
const selectedModelsIds = ref<number[]>([]);
const tradeoffExplanations = ref<{ reportId: string; explanation: string }[]>([]);

const comparison = useTemplateRef<InstanceType<typeof EstimatorReportComparison>>("comparison");

async function loadReports() {
  isLoading.value = true;

  if (route.query.reportId) {
    // get all concerned ids
    const ids = ([] as LocationQueryValue[])
      .concat(route.query.reportId)
      .map((v) => (v ? parseInt(v) : null))
      .filter((v) => v !== null);

    // fetch reports and filter potentially not found
    const results = (
      await Promise.all(
        ids.map((id) => api.fetchEstimatorReport(workspacePublicId.value, projectName.value, id))
      )
    ).filter((r) => r !== null);
    reports.value = results;

    // render
    isLoading.value = false;
  }
}

onMounted(async () => {
  await loadReports();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      {
        label: workspacePublicId,
        to: { name: RouteNames.Projects, params: { workspacePublicId } },
        icon: 'icon-masonry',
      },
      {
        label: projectName,
        to: {
          name: RouteNames.ProjectDetail,
          params: { workspacePublicId, projectName, section: ProjectDetailSections.Comparisons },
        },
        icon: 'icon-punch-card',
        iconColor: 'var(--color-purple-500)',
      },
      {
        label: 'Comparison',
        icon: 'icon-scale',
      },
    ]"
  >
    <template #default>
      <div class="comparison-builder">
        <div class="back text-base-medium" @click="router.back()">
          <i class="icon icon-arrow-left" /> Model finder
        </div>
        <div v-if="isLoading" class="loading">
          <SkoreSpinner />
        </div>
        <div v-else class="builder">
          <EstimatorReportComparison
            ref="comparison"
            :reports="reports"
            :is-read-only="false"
            v-model:tradeoff-explanations="tradeoffExplanations"
            v-bind:selected-models-ids="selectedModelsIds"
          />
        </div>
      </div>
    </template>
    <template #bottomBanner>
      <div class="comparison-save">
        <FloatingTooltip :enabled="!auth.isAuthenticated" text="Register to save your comparison">
          <SimpleButton
            label="Save comparison"
            @click="comparison?.saveComparison()"
            :disabled="selectedModelsIds.length === 0"
          />
        </FloatingTooltip>
      </div>
    </template>
  </DefaultPageLayout>
</template>

<style scoped>
.comparison-builder {
  display: flex;
  height: 100%;
  flex-direction: column;
  padding-bottom: var(--numbers-72);
  gap: var(--numbers-16);

  .back {
    margin-top: var(--numbers-32);
    color: var(--content-secondary);
    cursor: pointer;

    .icon {
      margin-right: var(--numbers-8);
      vertical-align: middle;
    }
  }

  .loading {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  .builder {
    overflow: hidden;
    height: 100%;
    flex-grow: 1;
  }
}

.comparison-save {
  text-align: right;
}
</style>
